<template>
	<div class="col-12 px-0 my-4">
		<OopsBanner2 v-if="showOopsBanner" />
		<div v-else>
			<h3 v-if="prestations.length > 0 && prestationsToShow.length > 0 && !activePrestations" class="title-4 text-grey-800 mb-2">
				{{ stepNum }}{{ store.state.content.planner_prestationsTitle }}
			</h3>
			<div class="prestations-list" style="display: block; opacity: 1" ref="prestationsList">
				<template v-for="(prestation, prestation_i) in prestationsToShow" :key="prestation.id || prestation_i">
					<PrestationComp ref="prestation" :prestation="prestation" />
				</template>
			</div>
			<div v-if="prestationsToShow.length === 0 && checkIfCompanyIsCgosException()">
				<OopsBanner />
			</div>

			<div v-if="!activePrestations" class="d-flex justify-content-center">
				<BlueBtn v-if="prestations.length > defaultNumberOfPrestationsToShow"
					@click="handleSeeMorePrestationsClick">
					<span class="link-default">
						{{
							!isShowingAllPrestations
								? store.state.content.planner_seeMoreHebergements
								: store.state.content.liheDesc_seeLess
						}}
					</span>
					<span class="close-icon" v-if="!isShowingAllPrestations">
						<svg width="14" height="14" class="icon">
							<use xlink:href="#___icon___chevron-down-bold"></use>
						</svg>
					</span>
					<span class="close-icon" v-else>
						<svg width="14" height="14" class="icon">
							<use xlink:href="#___icon___chevron-up-bold"></use>
						</svg>
					</span>
				</BlueBtn>
			</div>
		</div>

	</div>
</template>

<script lang="ts" setup>
import {
	ref,
	Ref,
	nextTick,
	watch,
	onMounted,
	computed,
	ComputedRef,
} from "vue"
//import store from "@/vue/planning/store/store"
import PrestationComp from "@/vue/planning/components/result/prestation.vue"
import OopsBanner from "@/vue/planning/components/oopsBanner.vue"
import OopsBanner2 from "@/vue/planning/components/oopsBanner2.vue"
import {
	defaultNumberOfPrestationsToShow_Desktop,
	additionalNumberOfPrestationsToShow_Desktop,
	defaultNumberOfPrestationsToShow_Mobile,
	additionalNumberOfPrestationsToShow_Mobile,
} from "@/config/plannerConfig"
import BlueBtn from "@/vue/common/BlueBtn.vue"
import { checkIfCompanyIsCgosException } from "@/utils/utils"

// Get cookies1`
import { checkIfCompanyIsCgos, getCookie } from "@/utils/utils"

// types
import { Prestation } from "@/types/index"

// computed
import availableTransportPackCodes from "@/vue/planning/computed/availableTransportPackCodes"

// Tracking
import trackViewItem from "@/tracking/trackItems"

import { useStore } from "vuex"
import { prestationHasWiypPrice } from "@/vue/planning/logic/prestationHasWiypPrice"
import getSiteFromDomain from "../logic/getSiteFromDomain"
const store = useStore();

// refs
const prestationsList = ref()
const isAnimating: Ref<boolean> = ref(false)
const queue: Ref<Array<any>> = ref([])
const prestations: Ref<Array<Prestation>> = ref([])
const readyPrestations: Ref<Array<Prestation>> = ref([])
const additionalPrestationsNum: Ref<number> = ref(0)

// computed
const activePrestations: ComputedRef<Array<string> | null> = computed(() => store.state.activePrestations)
const prestFromCatalog: ComputedRef<Array<Prestation> | null> = computed(() => store.state.prestationsFromCatalog)
const isPackageWithWiyp = computed(
	() => Boolean(store.state.packCode)
)

const defaultNumberOfPrestationsToShow = computed(() => {
	if (store.state.screenType === "mobile") {
		return defaultNumberOfPrestationsToShow_Mobile
	}

	return defaultNumberOfPrestationsToShow_Desktop
})
const additionalNumberOfPrestationsToShow = computed(() => {
	if (store.state.screenType === "mobile") {
		return additionalNumberOfPrestationsToShow_Mobile
	}
	return additionalNumberOfPrestationsToShow_Desktop
})
const stepNum = computed(() => {
	if (
		getSiteFromDomain(window.location.hostname) === "www.travelski.com" &&
		availableTransportPackCodes.value.length > 0 &&
		store.state.screenType === "desktop"
	)
		return "3. "
	return "2. "
})
const prestationsToShow: ComputedRef<Array<Prestation>> = computed(() => {
	let result: Array<Prestation> = []

	if (store.getters.locale === "fr_FR_CSE" && checkIfCompanyIsCgos()) {

		if (checkIfCompanyIsCgosException()) {
			result = prestations.value.filter(prestation => {
				if (!prestation.capacityMin || prestation.capacityMin > (store.state.participants || []).length) {
					return false;
				}
				return prestation;
			});
		}
		else {

			const prestationsWithCapacityLimitation = prestations.value.filter(
				(prestation) => prestation.capacity <= 8
			)

			result = prestationsWithCapacityLimitation.slice(
				0,
				defaultNumberOfPrestationsToShow.value + additionalPrestationsNum.value
			)
		}

	} else {
		result = prestations.value.slice(
			0,
			defaultNumberOfPrestationsToShow.value + additionalPrestationsNum.value
		)
	}

	if (prestFromCatalog.value != null) {
		result = prestFromCatalog.value
	}

	const filteredResult = result.filter(
		(prestation) => Object.keys(prestation).length > 0
	)

	if (!isPackageWithWiyp.value) {
		return filteredResult
	}

	// Keep skeleton placeholders visible until WIYP for the current package finishes.
	if (!store.state.calledPrice) {
		return getEmptyPrestations()
	}

	return filteredResult.filter((prestation) => prestationHasWiypPrice(prestation))
})

const showOopsBanner = computed(() => {
	if (store.state.loading) return false

	// Wait for WIYP before deciding there is nothing to show (package flows).
	if (isPackageWithWiyp.value && !store.state.calledPrice) return false

	return prestationsToShow.value.length === 0
})

const isShowingAllPrestations = computed(() => {
	return (
		store.getters.prestations.length <
		defaultNumberOfPrestationsToShow.value + additionalPrestationsNum.value
	)
})

// methods
const scrollToPlannerStart = () => {
	window.dispatchEvent(
		new CustomEvent("smoothScrollToSelector", {
			detail: {
				selector: "#tf-planning",
			},
		})
	)
}

const getEmptyPrestations = (): Array<Prestation> => {
	// controls the number of placeholders to show during loading
	if (store.state.screenType === "mobile") {
		return []
	} else if (store.state.screenType === "tablet") {
		return [{}]
	} else {
		return [{}, {}, {}]
	}
}

const updateAnimations = (): void => {
	// Exit early if animations were cancelled
	if (!isAnimating.value) {
		return
	}

	if (queue.value.length === 0) {
		isAnimating.value = false
		return
	}

	const newPrestations = queue.value.shift() // pops object from beginning of array
	queue.value = [...queue.value]

	if (
		prestations.value.length === 0 ||
		Object.keys(prestations.value[0]).length === 0
	) {
		if (
			newPrestations.length === 0 ||
			Object.keys(newPrestations[0]).length === 0
		) {
			updateAnimations()
			return
		}
	}

	if (prestationsList.value) {
		prestationsList.value.style.opacity = 0
	}

	setTimeout(() => {
		// Check again if animations were cancelled during the timeout
		if (!isAnimating.value) {
			return
		}

		if (store.getters.locale === "fr_FR_CSE" && checkIfCompanyIsCgos()) {
			// FIXME this is horrible, who did this?!
			const maxCapacity = parseInt(getCookie("maxCapacity") ?? "8")
			const filteredPrestations = newPrestations.filter(
				(prestation: Prestation) => {
					if (prestation.maxCapacity) {
						return prestation.maxCapacity <= maxCapacity
					} else if (prestation.capacity) {
						return prestation.capacity <= maxCapacity
					} else {
						return true
					}
				}
			)
			//prestations.value = [...filteredPrestations];
			updatePrestations(filteredPrestations)
		} else {
			//prestations.value = [...newPrestations]
			updatePrestations(newPrestations)
		}

		if (prestationsList.value) {
			prestationsList.value.style.opacity = 1
		}
		setTimeout(() => {
			// Check again before recursing
			if (!isAnimating.value) {
				return
			}
			updateAnimations()
			return
		}, 100)
	}, 100)
}

const triggerUpdateAnimations = (): void => {
	if (isAnimating.value) return

	isAnimating.value = true
	updateAnimations()
	store.state.prestationsLoaded = true
}

const setIntersectionObserver = (): void => {
	const options = {
		root: null,
		threshold: 0.5,
	}
	const callback = (entries: any) => {
		entries.forEach((entry: any) => {
			if (entry.isIntersecting) {
				if (!store.state.scrolledToPlanning && store.state.prestationsLoaded) {
					trackViewItem(
						store.getters.prestations,
						"view_item",
						store.state.packCode,
						store.state.averageRatingPerLihe,
						store.state.reviewNumberPerLihe,
						store.getters.cheapestPrestationIndex
					)
				}
				if (store.state.prestationsLoaded) {
					store.state.scrolledToPlanning = true
				}
			}
		})
	}
	const observer = new IntersectionObserver(callback, options)
	const target = document.querySelector("#tf-planning")
	if (target) {
		observer.observe(target)
	}
}

const handleSeeMorePrestationsClick = () => {
	if (isShowingAllPrestations.value) {
		additionalPrestationsNum.value = 0
		scrollToPlannerStart()
	} else {
		additionalPrestationsNum.value += additionalNumberOfPrestationsToShow.value
	}
}

const updatePrestations = (newPrestations: Array<Prestation>) => {
	if (isPackageWithWiyp.value && !store.state.calledPrice) {
		prestations.value = getEmptyPrestations()
		return
	}

	prestations.value = newPrestations.filter((prestation) => {
		// if (store.state.packCode === "HS") return true
		if (!store.state.packageId) return false
		if (!store.state.calledPrice) return false
		return prestationHasWiypPrice(prestation)
	})
}

watch(
	() => store.getters.prestations,
	(newPrestations: Array<Prestation>) => {
		const isPackage = store.state.packCode && store.state.packCode !== "HS"

		// If the prices endpoint was not called yet and it's a package, return
		if (isPackage && !store.state.calledPrice) return

		const nextPrestations =
			newPrestations.length > 0 ? newPrestations : getEmptyPrestations()

		// We only want the "blinking" skeleton transition when a full
		// availability search is running (state.loading is true).
		// For price-only/WIYP updates (loading is false), update prestations
		// directly without going through the animated queue to avoid blinking.
		if (store.state.loading) {
			queue.value = [...queue.value, nextPrestations]
		} else {
			updatePrestations(nextPrestations)
		}

		if (newPrestations && newPrestations.length) {
			trackViewItem(
				newPrestations,
				"view_item",
				store.state.packCode,
				store.state.averageRatingPerLihe,
				store.state.reviewNumberPerLihe,
				store.getters.cheapestPrestationIndex
			)
		}
	}
)

watch(
	() => store.state.loading,
	(newVal: boolean, oldVal: boolean) => {
		if (newVal && !oldVal) {
			queue.value = [...queue.value, getEmptyPrestations()]
		} else if (!newVal && oldVal) {
			// Availability search just finished.
			// Ensure we clear any remaining skeleton queue and show the latest prestations.
			const latestPrestations = store.getters.prestations as Array<Prestation>
			const nextPrestations =
				latestPrestations.length > 0 ? latestPrestations : getEmptyPrestations()

			// Stop animations and clear queued skeletons
			isAnimating.value = false
			queue.value = []

			// Ensure opacity is reset immediately to prevent skeleton from staying
			if (prestationsList.value) {
				prestationsList.value.style.opacity = 1
			}

			// Render the final prestations without additional blinking
			updatePrestations(
				latestPrestations.length > 0 ? latestPrestations : []
			)
			readyPrestations.value = [...nextPrestations]
		}
	}
)

watch(
	() => store.state.calledPrice,
	(calledPrice) => {
		if (!calledPrice) return

		const sourcePrestations =
			prestFromCatalog.value != null
				? prestFromCatalog.value
				: readyPrestations.value.length > 0
					? readyPrestations.value
					: (store.getters.prestations as Array<Prestation>)

		if (sourcePrestations.length > 0) {
			updatePrestations(sourcePrestations)
		}
	}
)

watch(
	// Watch the full wiypCall map: with fusion products, HS prices arrive under
	// multiple packageIds (e.g. "294HS" and "184211HS"). Watching only
	// store.state.packageId would miss the fusion WIYP response and keep that
	// prestation filtered out.
	() => [store.state.wiypCall, readyPrestations.value, prestFromCatalog.value],
	() => {
		if (!store.state.calledPrice) return

		const sourcePrestations =
			prestFromCatalog.value != null
				? prestFromCatalog.value
				: readyPrestations.value

		if (sourcePrestations.length === 0) return

		updatePrestations(sourcePrestations)
	},
	{ deep: true }
)


watch(
	() => queue.value,
	() => {
		nextTick(triggerUpdateAnimations)
		// Will fire the dispatch for refreshDataStoredOnWindow, the listener is on documentReady
		window.dispatchEvent(new Event("refreshDataStoredOnWindow"))
	}
)

watch(
	() => store.state.transportCheckbox,
	() => {
		updatePrestations(readyPrestations.value)
	}
)

onMounted(() => {
	prestations.value = getEmptyPrestations()
})
</script>

<style scoped>
.prestations-list {
	transition: opacity 100ms ease-in-out;
}
</style>
